import React, { Fragment, useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ReactModal from "react-modal";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import Select from "react-select";

import shortenName from "../../helpers/shorten-name";
import currencyFormatter from "../../helpers/currencyFormatter";
import OperatorAutocomplete from "../opi/operator-autocomplete";
import TextAreaInput from "../textarea-input";
import ai from "../../helpers/axios";
import { LoadingButton } from "../shared/loading-button";

const toastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "dataBeganDownloading": {
      return {
        ...state,
        isLoading: true,
      };
    }

    case "dataDownloaded": {
      return {
        ...state,
        isLoading: false,
        data: action?.payload?.data?.data || [],
        totalLength: action?.payload?.data?.countTotal,
      };
    }

    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    case "openModal": {
      return {
        ...state,
        showModal: true,
      };
    }

    case "closeModal": {
      return {
        ...state,
        showModal: false,
        selectedOperator: null,
        disruptionCause: null,
        delays: null,
        aircraftChanges: null,
        otherIssues: [],
        customOtherIssuesDetails: "",
      };
    }

    default:
      break;
  }
  return state;
};

const initialState = {
  showModal: false,
  saving: false,
  tripOFROperators: [],
  selectedOperator: null,
  disruptionCause: null,
  delays: null,
  aircraftChanges: null,
  otherIssues: [],
  customOtherIssuesDetails: "",
  isLoading: true,
  data: [],
  totalLength: 0,
  refreshDataTimestamp: Date.now(),
};

const updatedFieldsLabels = {
  "operator": "Operator",
  "aircraft": "Aircraft",
  "tailNumber": "Tail #",
  "operatorPrice": "Operator Price",
  "grossCommission": "Gross Commission",
  "fet": "FET",
  "segmentFees": "Seg. Fees",
  "referralFees": "Referral Fees",
  "discount": "Discount",
};

const disruptionCausesOptions = [
  {
    label: "Mechanical",
    value: "Mechanical",
  },
  {
    label: "Crew/Ops",
    value: "Crew/Ops",
  },
  {
    label: "Other (legitimate)",
    value: "Other (legitimate)",
  },
  {
    label: "Other (shenanigans)",
    value: "Other (shenanigans)",
  },
];

const delaysOptions = [
  {
    label: "No delay",
    value: "No delay",
  },
  {
    label: "0-60min",
    value: "0-60min",
  },
  {
    label: "1-2hr",
    value: "1-2hr",
  },
  {
    label: "2-4hr",
    value: "2-4hr",
  },
  {
    label: "4+hr (same day)",
    value: "4+hr (same day)",
  },
  {
    label: "Departed next day or later",
    value: "Departed next day or later",
  },
];

const aircraftChangesOptions = [
  {
    label: "The tail did not change - used the original aircraft",
    value: "The tail did not change - used the original aircraft",
  },
  {
    label: "New aircraft - SAME MODEL",
    value: "New aircraft - SAME MODEL",
  },
  {
    label: "New aircraft - SAME CLASS DIFFERENT MODEL",
    value: "New aircraft - SAME CLASS DIFFERENT MODEL",
  },
  {
    label: "New aircraft - LARGER CLASS",
    value: "New aircraft - LARGER CLASS",
  },
  {
    label: "New aircraft - SMALLER CLASS",
    value: "New aircraft - SMALLER CLASS",
  },
];

const otherIssuesOptions = [
  "Airport/Routing Change",
  "Baggage/pax unaccommodated",
  "Aircraft performance issues",
  "Aircraft amenities issues",
  "Ground trans/catering issues",
  "Aircraft condition",
  "Crew bad behavior",
  "Custom Issues",
];

const DisruptionsTableModuleUpdatedSignedOFRNestedRow = ({ data }) => {
  const diffList = useMemo(
    () => {
      const formatValue = (field, value) => {
        if (field === "tailNumber") {
          return value;
        }

        if (["operator", "aircraft"].includes(field)) {
          return value?.name || "N/A";
        }

        if (["operatorPrice", "grossCommission", "discount", "referralFees"].includes(field)) {
          return currencyFormatter.format(value) || "N/A";
        }

        if (["fet", "segmentFees"].includes(field)) {
          return `${value?.label || ""} ${value?.amount ? currencyFormatter.format(value.amount) : ""}`;
        }

        return null;
      };

      return Object.values((data?.diffList || []).reduce((acc, diff) => {
        if (diff.path.length === 1) {
          acc[diff.path[0]] = {
            field: diff.path[0],
            oldValue: diff.lhs,
            newValue: diff.rhs,
          };
        }

        if (diff.path.length === 2) {
          if (!acc[diff.path[0]]) {
            acc[diff.path[0]] = {
              field: diff.path[0],
              oldValue: {},
              newValue: {},
            };
          }

          acc[diff.path[0]].oldValue[diff.path[1]] = diff.lhs;
          acc[diff.path[0]].newValue[diff.path[1]] = diff.rhs;
        }

        return acc;
      }, {})).map(diff => ({
        field: diff.field,
        label: updatedFieldsLabels[diff.field] || "",
        oldValue: formatValue(diff.field, diff.oldValue),
        newValue: formatValue(diff.field, diff.newValue),
      }));
    },
    [data?.diffList],
  );

  return (
    <Table aria-label="collapsible table">
      <TableHead className="fm-l2-child-table-head dynamic-accordion-table-header">
        <TableRow className="fm-l2-child-table-head-row">
          <TableCell>
            Field
          </TableCell>
          <TableCell>
            Old Value
          </TableCell>
          <TableCell>
            New Value
          </TableCell>
        </TableRow>
      </TableHead>

      <TableBody className="fm-l2-child-table-body">
        {diffList.map((diff) => (
          <TableRow
            key={diff.field}
            className="fm-l2-childt-table-row"
          >
            <TableCell>
              {diff.label || ""}
            </TableCell>
            <TableCell>
              {diff.oldValue || "N/A"}
            </TableCell>
            <TableCell>
              {diff.newValue || "N/A"}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const DisruptionsTableModuleManualDisruptionNestedRow = ({ data }) => {
  return (
    <Grid container spacing={0}>
      <Grid item xs={12} sm={12} md={6} lg={4}>
        <h6>Delays</h6>

        <div>{data.metadata?.delays}</div>
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={4}>
        <h6>Aircraft Changes</h6>

        <div>{data.metadata?.aircraftChanges}</div>
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={4}>
        <h6>Other Issues</h6>

        {data.metadata?.otherIssues?.map(otherIssue => (
          <div key={otherIssue}>
            {otherIssue}

            {otherIssue === "Custom Issues" && data.metadata.customOtherIssuesDetails && (<span>: {data.metadata.customOtherIssuesDetails}</span>)}
          </div>
        ))}
      </Grid>
    </Grid>
  );
};

const DisruptionsTableModuleRow = ({ data }) => {
  const [isOpen, setIsOpen] = useState(false);

  const isExpandable = useMemo(
    () => ["signedOfrDataUpdated", "addManualDisruption"].includes(data?.eventType),
    [data],
  );

  const toggleOpen = useCallback((event) => {
    event.stopPropagation();

    if (!isExpandable) {
      return;
    }

    setIsOpen(open => !open);
  }, [isExpandable]);

  const cause = useMemo(() => {
    if (!data || !data.metadata) {
      return null;
    }

    if (data.eventType === "signedOfrDataUpdated") {
      return data.metadata.editReason;
    }

    if (["cancelYesRefund", "cancelNoRefund"].includes(data.eventType)) {
      return data.metadata.cancellationReason;
    }

    if (data.eventType === "addManualDisruption") {
      return data.metadata.disruptionCause;
    }

    return null;
  }, [data]);

  if (!data) {
    return null;
  }

  return (
    <Fragment>
      <TableRow
        sx={{ "& > *": { borderBottom: "unset" } }}
        onClick={toggleOpen}
        className="fm-l2-parent-table-row"
      >
        <TableCell>
          {isExpandable && (
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={toggleOpen}
              className="p-0"
            >
              {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{shortenName(data.metadata?.user?.name)}</TableCell>
        <TableCell>
          {moment(data.timestamp).format("M/D/YY - h:mm a")}
        </TableCell>
        <TableCell>{data.metadata?.operator?.name || "N/A"}</TableCell>
        <TableCell>{cause || "N/A"}</TableCell>
      </TableRow>

      {isExpandable && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1 }}>
                {data.eventType === "signedOfrDataUpdated" &&
                  <DisruptionsTableModuleUpdatedSignedOFRNestedRow data={data} />}
                {data.eventType === "addManualDisruption" &&
                  <DisruptionsTableModuleManualDisruptionNestedRow data={data} />}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
};

const DisruptionsTableModule = (props) => {
  const { tripId, tripData, readOnly } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  useEffect(() => {
    props.setUnsavedChanges?.({
      section: 'disruptionsModal',
      value: !!state.showModal
    });
  }, [props.setUnsavedChanges, state.showModal]);

  useEffect(() => {
    if (tripData?.operatorFinancialRecords?.length) {
      const operators = Array.from(
        new Map(tripData.operatorFinancialRecords.map(ofr => ofr.operator).map(item => [item.id, item])).values(),
      );

      dispatch({
        type: "fieldChanged",
        field: "tripOFROperators",
        value: operators.map(operator => ({
          label: operator.name,
          value: operator.name,
          operatorName: operator.name,
          _id: operator.id,
          ...operator,
        })),
      });
    }
  }, [tripData?.operatorFinancialRecords]);

  const isSelectedOperatorUnknown = useMemo(() => {
    if (!state.selectedOperator) {
      return false;
    }

    return !state.tripOFROperators.find(operator => operator.id === state.selectedOperator.id);
  }, [state.selectedOperator, state.tripOFROperators]);

  const isCustomIssuesSelected = useMemo(
    () => state.otherIssues.includes("Custom Issues"),
    [state.otherIssues],
  );

  const handleAddManualDisruptionEvent = useCallback(() => {
    dispatch({
      type: "openModal",
    });
  }, []);

  const handleCloseModal = useCallback(() => {
    dispatch({
      type: "closeModal",
    });
  }, []);

  const handleOperatorChange = useCallback((selectedOperator) => {
    dispatch({
      type: "fieldChanged",
      field: "selectedOperator",
      value: selectedOperator,
    });
  }, []);

  const handleDisruptionCauseChange = useCallback((selectedValue) => {
    dispatch({
      type: "fieldChanged",
      field: "disruptionCause",
      value: selectedValue,
    });
  }, []);

  const handleDelaysChange = useCallback((selectedValue) => {
    dispatch({
      type: "fieldChanged",
      field: "delays",
      value: selectedValue,
    });
  }, []);

  const handleAircraftChangesChange = useCallback((selectedValue) => {
    dispatch({
      type: "fieldChanged",
      field: "aircraftChanges",
      value: selectedValue,
    });
  }, []);

  const handleOtherIssuesChange = useCallback((event) => {
    const { name, checked } = event.target;
    const [, , value] = name.match(/^([a-zA-Z_]\w*)\[([\w\s/]+)\]$/) || [];

    if (value === "none") {
      dispatch({
        type: "fieldChanged",
        field: "otherIssues",
        value: [],
      });
    } else {
      dispatch({
        type: "fieldChanged",
        field: "otherIssues",
        value: checked ? [...state.otherIssues, value] : [...state.otherIssues].filter(issue => issue !== value),
      });
      if (value === "Custom Issues" && !checked) {
        dispatch({
          type: "fieldChanged",
          field: "customOtherIssuesDetails",
          value: "",
        });
      }
    }
  }, [state.otherIssues]);

  const handleInputChange = useCallback((e) => {
    dispatch({
      type: "fieldChanged",
      field: e.currentTarget.name,
      value: e.currentTarget.value,
    });
  }, []);

  const handleSubmitManualDisruptionEvent = useCallback(async (e) => {
    e.preventDefault();

    if (!state.selectedOperator?.value || !state.disruptionCause?.value || !state.delays?.value || !state.aircraftChanges?.value || (isCustomIssuesSelected && !state.customOtherIssuesDetails)) {
      return toast.error(
        "Please fill in all required fields and retry.",
        toastOptions,
      );
    }

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });

    const response = await ai
      .auth(token)
      .put(`/api/trips/${tripId}/addManualDisruption`, {
        operator: {
          id: state.selectedOperator.id || state.selectedOperator._id,
          name: state.selectedOperator.value,
        },
        disruptionCause: state.disruptionCause.value,
        delays: state.delays.value,
        aircraftChanges: state.aircraftChanges.value,
        otherIssues: state.otherIssues.length ? state.otherIssues : undefined,
        customOtherIssuesDetails: isCustomIssuesSelected ? state.customOtherIssuesDetails : undefined,
      })
      .catch((error) => {
        return console.error(error.response);
      });

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: false,
    });

    if (response) {
      handleCloseModal();
      dispatch({ type: "fieldChanged", field: "refreshDataTimestamp", value: Date.now() });
    }
  }, [
    tripId,
    state.selectedOperator,
    state.disruptionCause,
    state.delays,
    state.aircraftChanges,
    state.otherIssues,
    state.customOtherIssuesDetails,
    getAccessTokenSilently,
    loginWithRedirect,
    isCustomIssuesSelected,
    handleCloseModal,
  ]);

  const getData = async () => {
    dispatch({ type: "dataBeganDownloading" });

    const token = await getAccessTokenSilently().catch(loginWithRedirect);

    const { data } = await ai.auth(token).get(`/api/trips/${tripId}/disruptions`, {
      params: {},
    });

    dispatch({
      type: "dataDownloaded",
      payload: {
        data,
      },
    });
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.refreshDataTimestamp]);

  return (
    <Fragment>
      <div className="trip-notes-module">
        <h5 className="mb-1 d-flex">
          Disruptions

          {!readOnly && (
            <button
              className="base-button icon-button icon-button-small evo-dark-green ml-2"
              onClick={handleAddManualDisruptionEvent}
              title="Add"
            >
              <span className="material-icons">add_circle</span>
            </button>
          )}
        </h5>

        <TableContainer
          component={Paper}
          className="accordion-table-with-details shadow-none"
          id="trip-disruptions"
        >
          <Table aria-label="collapsible table">
            <TableHead className="fm-l2-parent-table-head dynamic-accordion-table-header">
              <TableRow className="fm-l2-parent-table-head-row">
                <TableCell />
                <TableCell>
                  Created By
                </TableCell>
                <TableCell>
                  Entered At
                </TableCell>
                <TableCell>
                  Operator
                </TableCell>
                <TableCell>
                  Cause
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody className="fm-l2-parent-table-body">
              {!state.data.length && (
                <TableRow
                  className="fm-l2-parent-table-row"
                >
                  <TableCell colSpan={5}>No disruptions registered yet</TableCell>
                </TableRow>
              )}

              {state.data.length > 0 && state.data.map((disruption, index) => (
                <DisruptionsTableModuleRow
                  key={`disruptions-table-module-row-${index}`}
                  data={disruption}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <ReactModal
        isOpen={state?.showModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            border: "none",
            maxWidth: "700px",
            maxHeight: "80vh",
            minWidth: "calc(min(500px, 95vw))",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>
            Add Disruption Event
          </h4>
        </div>

        <div className="modal-body base-modal modal-body-with-scroll">
          <div className="w-100 mt-20">Please provide all the details for the disruption event.</div>

          <form
            onSubmit={handleSubmitManualDisruptionEvent}
            className="w-100 base-form mt-20"
          >
            <OperatorAutocomplete
              handleChange={handleOperatorChange}
              operatorOptions={state.tripOFROperators}
              parentDispatch={dispatch}
              selectedOperator={state.selectedOperator}
              containerClasses="flex-grow-1"
              required
            />
            {isSelectedOperatorUnknown && (
              <FormHelperText>No OFR exists for this Operator – provide details below</FormHelperText>)}

            <div className="form-group">
              <label htmlFor="disruptionCause" className="base-label">
                Disruption Cause <span className="required">*</span>
              </label>

              <FormHelperText className="mt-0 mb-10">
                If more than one cause is relevant, choose the prevailing cause.
              </FormHelperText>

              <Select
                className="disruptionCause-select"
                classNamePrefix="select"
                options={disruptionCausesOptions}
                placeholder="Select Cause"
                onChange={handleDisruptionCauseChange}
                value={state.disruptionCause}
                name="disruptionCause"
                required
                menuPosition="fixed"
              />
            </div>

            <h5 className="mt-4">Disruption Result</h5>
            <div className="form-group">
              <label htmlFor="delays" className="base-label">
                Delays <span className="required">*</span>
              </label>

              <Select
                className="delays-select"
                classNamePrefix="select"
                options={delaysOptions}
                placeholder="Select Delays"
                onChange={handleDelaysChange}
                value={state.delays}
                name="delays"
                required
                menuPosition="fixed"
              />
            </div>
            <div className="form-group">
              <label htmlFor="aircraftChanges" className="base-label">
                Aircraft Changes <span className="required">*</span>
              </label>

              <FormHelperText className="mt-0 mb-10">
                Which situation best describes what happened?
              </FormHelperText>

              <Select
                className="aircraftChanges-select"
                classNamePrefix="select"
                options={aircraftChangesOptions}
                placeholder="Select Aircraft Changes"
                onChange={handleAircraftChangesChange}
                value={state.aircraftChanges}
                name="aircraftChanges"
                required
                menuPosition="fixed"
              />
            </div>

            <div className="form-group">
              <label className="base-label">
                Other Issues
              </label>

              <div className="checkbox-list">
                <div>
                  <input
                    type="checkbox"
                    name="otherIssues[none]"
                    id="otherIssues[none]"
                    onChange={handleOtherIssuesChange}
                    checked={!state.otherIssues.length}
                    disabled={!state.otherIssues.length}
                  />
                  <label htmlFor="otherIssues[none]">
                    No other issues
                  </label>
                </div>
                {otherIssuesOptions.map((issue) => (
                  <div key={issue}>
                    <input
                      type="checkbox"
                      name={`otherIssues[${issue}]`}
                      id={`otherIssues[${issue}]`}
                      onChange={handleOtherIssuesChange}
                      checked={state.otherIssues.includes(issue)}
                    />
                    <label htmlFor={`otherIssues[${issue}]`}>
                      {issue}
                    </label>
                  </div>
                ))}
              </div>

              {isCustomIssuesSelected && (
                <TextAreaInput
                  type="text"
                  name="customOtherIssuesDetails"
                  value={state.customOtherIssuesDetails}
                  hideLabel
                  placeholder="Please provide details"
                  handleChange={handleInputChange}
                  required={isCustomIssuesSelected}
                />
              )}
            </div>
          </form>
        </div>

        <div className="modal-footer w-100 mt-20 d-flex gap-20">
          <LoadingButton
            className="base-button evo-blue flex-half"
            type="button"
            onClick={handleSubmitManualDisruptionEvent}
            isLoading={state.saving}
          >
            Submit
          </LoadingButton>

          <button
            className="base-button btn-danger flex-half"
            type="button"
            onClick={handleCloseModal}
            disabled={state.saving}
          >
            Cancel
          </button>
        </div>
      </ReactModal>
    </Fragment>
  );
};

export default DisruptionsTableModule;
